// src/app/interceptors/error.interceptor.ts
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../../service/auth.service';
import { ErrorMessageService } from '../../services/error-message.service';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const authService = inject(AuthService);
  const errorMessageService = inject(ErrorMessageService);
  
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // La validation email AbstractAPI est optionnelle dans certains environnements.
      // Si l'endpoint n'existe pas (404) ou n'est pas disponible, le composant applique
      // un fallback non bloquant; on évite donc le toast d'erreur global.
      if (error.url?.includes('/api/comparaison/email/validate')) {
        return throwError(() => error);
      }

      // Laisser APICIL gérer ses propres messages détaillés (blob/json) côté service/composant.
      if (error.url?.includes('/api/v1/apicil/devis/') && error.url?.includes('/liasse/download')) {
        return throwError(() => error);
      }
      // Laisser ALPTIS (étude personnalisée) parser les erreurs blob/json côté service.
      if (error.url?.includes('/alptis/v1/etude-personnalisee')) {
        return throwError(() => error);
      }
      // Dashboard CAC: l'endpoint admin/users-status-stats peut être absent selon la version backend.
      // Le fallback est géré dans DashboardService, on préserve donc l'erreur HTTP originale.
      if (error.url?.includes('/api/dashboard/admin/users-status-stats')) {
        return throwError(() => error);
      }

      // CAS compare (assureurs): laisser les composants CAS afficher les messages métiers backend
      // (ex: "UTWIN accepte SSI pas TNS") au lieu d'un message générique global.
      const casCompareErrorBypassPaths = [
        // Routes comparateur
        '/api/compare',
        '/api/v1/compare',
        '/compare',
        '/cas/compare',
        // Routes directes assureurs
        '/api/utwin/',
        '/api/utwin',
        '/api/v1/utwin/',
        '/api/v1/apicil/',
        '/api/v1/alptis/',
        '/api/v1/neoliane/',
        '/api/v1/zenioo/',
        '/api/v1/generali/',
        '/api/v1/apivia/',
        '/api/v1/henner/',
        '/api/v1/swisslife/',
        '/api/v1/eca/',
        '/api/v1/april/',
        // Routes de tarification (SwissLife, ECA, APRIL, Alptis, etc. passent par /tarification/)
        '/api/v1/tarification/',
        '/api/tarification/',
        // Routes Flask (ECA backend Python)
        '/flask-api/',
        // Routes via /cas/api/ (Apivia, APRIL health protection)
        '/cas/api/',
        // Routes de comparaison et santé
        '/api/comparaison/',
        '/cas/api/healthProtection/'
      ];
      if (casCompareErrorBypassPaths.some(path => error.url?.includes(path))) {
        return throwError(() => error);
      }

      // Gérer l'expiration de session
      if (error.status === 401 && !req.url?.includes('/login')) {
        // L'agenda CAS gère ses propres erreurs (backend indisponible, droits, etc.)
        if (req.url?.includes('/agenda-cas')) {
          return throwError(() => error);
        }
        authService.handleTokenExpired();
        return throwError(() => error);
      }

      // Utiliser le service centralisé pour obtenir un message clair
      const { summary, detail } = errorMessageService.getErrorMessage(error);
      
      // Afficher le message d'erreur compréhensible (sans code HTTP)
      messageService.add({
        severity: 'error',
        summary: summary,
        detail: detail,
        life: 7000
      });
      
      // Log pour le débogage (gardé en console seulement)
      console.error('Error:', {
        url: error.url,
        status: error.status,
        message: detail
      });
      
      return throwError(() => new Error(detail));
    })
  );
};
